'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import WhatsApp from '@/components/whatsapp'
import { Phone as PhoneIcon, Pencil } from 'lucide-react'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'

type Props = {
  useWhatsApp: boolean
  phone: string
  onPhoneChange: (digits: string) => void
  primaryLabel: string
  primaryHelper: string
  dirtyPrimary: boolean
  setDirtyPrimary: (v: boolean) => void
  validPrimary: boolean

  otp: string
  setOtp: (v: string) => void
  otpSent: boolean
  otpSending: boolean
  otpVerified: boolean
  otpVerifying: boolean
  otpError: string | null
  onSendOtp: () => void
  onVerifyOtp: () => void
  existingPatientHumanId?: string | null

  showVerifiedMessage?: boolean

  // bumps each time a new OTP is successfully sent
  otpTimerKey: number
}

const OTP_TIMER_SECONDS = 120
const MAX_SENDS_PER_PHONE = 3 // keep in sync with backend

function formatMobile(digits: string) {
  if (!digits) return ''
  const first = digits.slice(0, 5)
  const last = digits.slice(5)
  return `+91 ${first}${last ? ' ' + last : ''}`
}

const PhoneOtpSection = ({
  useWhatsApp,
  phone,
  onPhoneChange,
  primaryLabel,
  primaryHelper,
  dirtyPrimary,
  setDirtyPrimary,
  validPrimary,
  otp,
  setOtp,
  otpSent,
  otpSending,
  otpVerified,
  otpVerifying,
  otpError,
  onSendOtp,
  onVerifyOtp,
  existingPatientHumanId,
  showVerifiedMessage = true,
  otpTimerKey,
}: Props) => {
  const hasValidPhone = /^\d{10}$/.test(phone)

  const [autoSentPhone, setAutoSentPhone] = useState<string | null>(null)
  const [lastAttemptedOtp, setLastAttemptedOtp] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number>(0)

  const [rateLimited, setRateLimited] = useState(false)
  const [showLimitModal, setShowLimitModal] = useState(false)

  const normalizedPhone = useMemo(
    () => phone.replace(/\D/g, '').slice(-10),
    [phone],
  )

  // clear rate-limit + timer when phone changes
  const prevPhoneRef = useRef(normalizedPhone)
  useEffect(() => {
    if (prevPhoneRef.current !== normalizedPhone) {
      setRateLimited(false)
      setRemaining(0)
      prevPhoneRef.current = normalizedPhone
    }
  }, [normalizedPhone])

  // backend rate-limit detection via otpError
  useEffect(() => {
    if (!otpError) {
      setRateLimited(false)
      return
    }
    const msg = otpError.toLowerCase()
    if (
      msg.includes('requested otp too many') ||
      msg.includes('too many requests')
    ) {
      setRateLimited(true)
    } else {
      setRateLimited(false)
    }
  }, [otpError])

  // local send count from otpTimerKey
  const localSendCount = otpTimerKey
  const frontLimitReached = localSendCount >= MAX_SENDS_PER_PHONE
  const limitReached = frontLimitReached || rateLimited

  // auto-send OTP when phone becomes valid (10 digits)
  useEffect(() => {
    if (!hasValidPhone || otpVerified || limitReached) {
      setAutoSentPhone(null)
      return
    }

    if (!otpSent && !otpSending && autoSentPhone !== phone) {
      onSendOtp()
      setAutoSentPhone(phone)
    }
  }, [
    hasValidPhone,
    otpSent,
    otpSending,
    autoSentPhone,
    phone,
    onSendOtp,
    otpVerified,
    limitReached,
  ])

  // start / restart timer when a new OTP is successfully sent
  useEffect(() => {
    if (!otpSent || otpTimerKey === 0) {
      setRemaining(0)
      return
    }

    setRemaining(OTP_TIMER_SECONDS)

    const id = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          window.clearInterval(id)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      window.clearInterval(id)
    }
  }, [otpSent, otpTimerKey])

  // auto-verify when 6-digit OTP entered, but only once per OTP value
  useEffect(() => {
    if (
      otp.length === 6 &&
      otpSent &&
      !otpVerified &&
      !otpVerifying &&
      otp !== lastAttemptedOtp
    ) {
      setLastAttemptedOtp(otp)
      onVerifyOtp()
    }
  }, [otp, otpSent, otpVerified, otpVerifying, lastAttemptedOtp, onVerifyOtp])

  const handleEditPhone = () => {
    onPhoneChange('')
    setDirtyPrimary(false)
    setOtp('')
    setLastAttemptedOtp(null)
    setRateLimited(false)
  }

  const handleResendClick = () => {
    const timeExpired = remaining <= 0

    // if timer still running → treat as disabled, do nothing
    if (!timeExpired) return

    // timer expired & we've hit the limit (front or back) → show modal
    if (limitReached) {
      setShowLimitModal(true)
      return
    }

    // timer expired & limit not reached → send new OTP
    setOtp('')
    setLastAttemptedOtp(null)
    onSendOtp()
  }

  const phoneLocked = otpSent || otpVerified

  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  const formattedTime = `${String(minutes).padStart(2, '0')}:${String(
    seconds,
  ).padStart(2, '0')}`

  const showOtpUI =
    hasValidPhone && !otpVerified && (otpSending || otpSent || limitReached)

  const shouldShowVerifiedText = otpVerified && showVerifiedMessage

  const timeExpired = remaining <= 0

  return (
    <>
      <div className="flex flex-col gap-2.5 sm:gap-3">
        {/* Phone input + (conditional) edit */}
        <div className="flex flex-col gap-1.5 sm:gap-2">
          <Label htmlFor="primary" className="text-sm sm:text-base">
            <span className="inline-flex items-center gap-1.5 sm:gap-2">
              {useWhatsApp ? (
                <WhatsApp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
              ) : (
                <PhoneIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
              {primaryLabel} <span className="text-red-600">*</span>
            </span>
          </Label>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Input
              id="primary"
              value={formatMobile(phone)}
              onChange={(e) => {
                let digits = e.target.value.replace(/\D/g, '')
                if (digits.startsWith('91')) digits = digits.slice(2)
                digits = digits.slice(0, 10)
                onPhoneChange(digits)
              }}
              onBlur={() => setDirtyPrimary(true)}
              placeholder="+91 12345 67890"
              maxLength={16}
              disabled={phoneLocked || otpSending}
              aria-invalid={dirtyPrimary && !validPrimary && phone.length > 0}
              className={`text-sm sm:text-base h-9 sm:h-10 ${
                dirtyPrimary && !validPrimary && phone.length > 0
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : ''
              }`}
            />

            {otpSent && !otpVerified && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleEditPhone}
                className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0"
              >
                <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>

          {dirtyPrimary && !validPrimary && phone.length > 0 && (
            <p className="text-[10px] sm:text-xs text-red-600">
              Enter a 10-digit {useWhatsApp ? 'WhatsApp' : 'phone'} number.
            </p>
          )}

          {primaryHelper && (
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              {primaryHelper}
            </p>
          )}
        </div>

        {/* OTP section */}
        {showOtpUI && (
          <div className="flex flex-col gap-2 sm:gap-2.5">
            {otpSending ? (
              <p className="text-[10px] sm:text-xs font-medium text-blue-600">
                Sending OTP...
              </p>
            ) : otpError ? (
              <p className="text-[10px] sm:text-xs font-medium text-red-600">
                {otpError}
              </p>
            ) : (
              <p className="text-[10px] sm:text-xs font-medium text-emerald-600">
                OTP sent successfully!
              </p>
            )}

            {/* Only show input and buttons if no rate limit error */}
            {!rateLimited && (
              <>
                {/* OTP input - scales with container */}
                <div
                  className="flex flex-wrap items-center gap-2 sm:gap-3"
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text/plain').trim()
                    const digits = text.replace(/\D/g, '').slice(0, 6)
                    if (digits.length > 0) {
                      e.preventDefault()
                      setOtp(digits)
                    }
                  }}
                >
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) =>
                      setOtp(value.replace(/\D/g, '').slice(0, 6))
                    }
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    autoFocus
                  >
                    <InputOTPGroup className="gap-1 sm:gap-1.5">
                      <InputOTPSlot
                        index={0}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                      <InputOTPSlot
                        index={1}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                      <InputOTPSlot
                        index={2}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                      <InputOTPSlot
                        index={3}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                      <InputOTPSlot
                        index={4}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                      <InputOTPSlot
                        index={5}
                        className="w-8 h-9 sm:w-10 sm:h-11 text-sm sm:text-base"
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {/* Timer + resend */}
                {otpSent && !otpSending && (
                  <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                    <span className="text-muted-foreground">
                      {remaining > 0
                        ? `OTP expires in: ${formattedTime}`
                        : 'OTP expired. You can request a new code.'}
                    </span>

                    <Button
                      type="button"
                      size="sm"
                      onClick={handleResendClick}
                      disabled={!timeExpired}
                      className={`h-7 sm:h-8 text-[10px] sm:text-xs px-2.5 sm:px-3 ${
                        !timeExpired
                          ? 'bg-transparent text-muted-foreground'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                      variant={timeExpired ? 'default' : 'ghost'}
                    >
                      {!timeExpired ? 'Resend OTP' : 'Send new OTP'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {shouldShowVerifiedText && (
          <p className="text-[10px] sm:text-xs text-emerald-600">
            Number verified successfully.
          </p>
        )}

        {existingPatientHumanId && (
          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
            Your ReACH ID:{' '}
            <span className="font-medium">{existingPatientHumanId}</span>
          </p>
        )}
      </div>

      {/* Rate-limit modal */}
      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-lg max-w-sm w-full">
            <h2 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">
              Too many requests
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
              You have requested OTP too many times for this number. Please try
              again later.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm h-8 sm:h-9"
                onClick={() => setShowLimitModal(false)}
              >
                Close
              </Button>
              <Button
                size="sm"
                className="text-xs sm:text-sm h-8 sm:h-9"
                onClick={() => {
                  window.location.href = '/'
                }}
              >
                Go to home
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PhoneOtpSection
